import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Alert, Modal } from 'react-bootstrap';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { DashboardStats, RevenueData } from '../../types';
import { apiService } from '../../services/api';
import './Dashboard.css';
import PlateRecognitionDisplay from '../../components/PlateRecognitionDisplay';

// 註冊 Chart.js 組件
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showPlateModal, setShowPlateModal] = useState<boolean>(true);

  // 載入儀表板數據
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const [statsResponse, revenueResponse] = await Promise.all([
        apiService.getDashboardStats(),
        apiService.getRevenueData(7)
      ]);

      if (statsResponse.success && statsResponse.data) {
        setStats(statsResponse.data);
      }

      if (revenueResponse.success && revenueResponse.data) {
        // Ensure revenueData is always an array
        setRevenueData(Array.isArray(revenueResponse.data) ? revenueResponse.data : []);
      } else {
        // Set empty array if no data or unsuccessful response
        setRevenueData([]);
      }
    } catch (error: any) {
      console.error('載入儀表板數據失敗:', error);
      setError('載入數據失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
    
    // 設置自動刷新
    const interval = setInterval(loadDashboardData, 30000); // 每30秒刷新一次
    
    return () => clearInterval(interval);
  }, []);

  // 停車位使用率圖表配置
  const occupancyChartData = {
    labels: ['已使用', '可用'],
    datasets: [
      {
        data: stats ? [stats.occupiedSpaces, stats.availableSpaces] : [0, 0],
        backgroundColor: ['#dc3545', '#28a745'],
        borderColor: ['#dc3545', '#28a745'],
        borderWidth: 2,
      },
    ],
  };

  const occupancyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      title: {
        display: true,
        text: '停車位使用狀況',
      },
    },
  };

  // 收入趨勢圖表配置
  const revenueChartData = {
    labels: Array.isArray(revenueData) ? revenueData.map(item => new Date(item.date).toLocaleDateString()) : [],
    datasets: [
      {
        label: '每日收入',
        data: Array.isArray(revenueData) ? revenueData.map(item => item.revenue) : [],
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 2,
      },
    ],
  };

  const revenueChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: '近7日收入趨勢',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value: any) {
            return '¥' + value.toLocaleString();
          }
        }
      },
    },
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">載入中...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="text-center">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </Alert>
    );
  }

  return (
    <div className="dashboard">
      {/* 車牌辨識彈窗 */}
      <Modal show={showPlateModal} onHide={() => setShowPlateModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-id-card me-2"></i>
            車牌辨識
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <PlateRecognitionDisplay showAllCameras={true} />
        </Modal.Body>
      </Modal>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="dashboard-title">
          <i className="fas fa-tachometer-alt me-3"></i>
          儀表板
        </h1>
        <div className="text-muted">
          <i className="fas fa-sync-alt me-2"></i>
          最後更新: {new Date().toLocaleTimeString()}
        </div>
      </div>

      {/* 統計卡片 */}
      <Row className="mb-4">
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="stat-card h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="stat-icon bg-primary">
                <i className="fas fa-car"></i>
              </div>
              <div className="ms-3">
                <div className="stat-value">{stats?.totalSpaces || 0}</div>
                <div className="stat-label">總停車位</div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="stat-card h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="stat-icon bg-success">
                <i className="fas fa-check-circle"></i>
              </div>
              <div className="ms-3">
                <div className="stat-value">{stats?.availableSpaces || 0}</div>
                <div className="stat-label">可用車位</div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="stat-card h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="stat-icon bg-warning">
                <i className="fas fa-dollar-sign"></i>
              </div>
              <div className="ms-3">
                <div className="stat-value">¥{stats?.todayRevenue?.toLocaleString() || 0}</div>
                <div className="stat-label">今日收入</div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="stat-card h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="stat-icon bg-info">
                <i className="fas fa-users"></i>
              </div>
              <div className="ms-3">
                <div className="stat-value">{stats?.activeRecords || 0}</div>
                <div className="stat-label">當前車輛</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 圖表區域 */}
      <Row>
        <Col xs={12} lg={8} className="mb-4">
          <Card className="chart-card h-100">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-bar me-2"></i>
                收入趨勢
              </h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Bar data={revenueChartData} options={revenueChartOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={4} className="mb-4">
          <Card className="chart-card h-100">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-chart-pie me-2"></i>
                停車位狀況
              </h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Doughnut data={occupancyChartData} options={occupancyChartOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;